/**
 * @file PsychosocialRiskListView.jsx
 * @author Daniel Aguirre
 * @description PsychosocialRisk List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';

import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function PsychosocialRiskListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, date, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getPsychosocialRiskListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('dashboardStaff.lastPsychologyValorationDate'),
      dataIndex: 'fecha_ultimo_registro_revision_psicologia',
      filter: {
        listName: 'listaFechaUltimaValoracionPsicologica',
        searchName: 'filtroUltimaValoracionPsicologica',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultimo_registro_revision_psicologia'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimaValoracionPsicologica',
            searchName: 'filtroUltimaValoracionPsicologica'
          },
          dataIndex: 'fecha_ultimo_registro_revision_psicologia'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.lastRiskPsychosocialDate'),
      dataIndex: 'fecha_ultimo_registro_riesgo_psicosocial_psicologia',
      filter: {
        listName: 'listaFechaUltimoRegistroRiesgoGeneralSocial',
        searchName: 'filtroUltimoRegistroRiesgoGeneralsocial',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus(
          'fecha_ultimo_registro_riesgo_psicosocial_psicologia'
        ),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'listaFechaUltimoRegistroRiesgoGeneralSocial',
            searchName: 'filtroUltimoRegistroRiesgoGeneralsocial'
          },
          dataIndex: 'fecha_ultimo_registro_riesgo_psicosocial_psicologia'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        filterCoincideSeleccionado: classification,
        date,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getPsychosocialRiskListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            fecha_ultimo_registro_revision_psicologia:
              item?.fecha_ultimo_registro_revision_psicologia
                ? formatDate(item.fecha_ultimo_registro_revision_psicologia)
                : '',
            fecha_ultimo_registro_riesgo_psicosocial_psicologia:
              item?.fecha_ultimo_registro_riesgo_psicosocial_psicologia
                ? formatDate(
                    item.fecha_ultimo_registro_riesgo_psicosocial_psicologia
                  )
                : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
PsychosocialRiskListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getPsychosocialRiskListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default PsychosocialRiskListView;
